import { css, html, nothing } from 'lit';
import { api } from '../../lib/api.js';
import { pinLabel, versionGroups } from '../../lib/asset-versions.js';
import { BaseComponent } from '../../lib/base-component.js';

/**
 * What each app serves for an asset (issue #217): one row per app with its pin
 * — the default (d3's latest), a source's latest, or one version of a source —
 * the version that resolves to, and a control to change or reset it. Changing
 * a pin needs settings:admin. The page calls setPin(null, …) to pin every app
 * (the versions table's "Pin for every app").
 */
class AppPinsPanel extends BaseComponent {
    static properties = {
        assetId: { type: String, attribute: 'asset-id' },
        versions: { type: Array },
        _apps: { type: Array, state: true },
        _editing: { type: String, state: true },
        _choice: { type: String, state: true },
        _busy: { type: Boolean, state: true },
        _error: { type: String, state: true },
    };

    static styles = css`
        :host { display: block; }
        table { font-size: 0.85rem; margin-bottom: 0; }
        .actions { white-space: nowrap; text-align: right; }
        .actions .btn { font-size: 0.75rem; padding: 0.1rem 0.45rem; }
        .muted { color: #6c757d; }
    `;

    constructor() {
        super();
        this.assetId = '';
        this.versions = [];
        this._apps = [];
        this._editing = '';
        this._choice = '';
        this._busy = false;
        this._error = '';
    }

    updated(changed) {
        if (changed.has('assetId') && this.assetId) this.reload();
    }

    async reload() {
        this._error = '';
        try {
            const data = await api.get(`/api/assets/${this.assetId}/pins`);
            this._apps = data.apps || [];
        } catch (err) {
            this._apps = [];
            this._error = err.message;
        }
    }

    /**
     * Pin one app (appId) or every app (null), or reset it to the default
     * when pin is null.
     * @param {string | null} appId
     * @param {{ source: string, version: number | null } | null} pin
     */
    async setPin(appId, pin) {
        const path = appId
            ? `/api/assets/${this.assetId}/pins/apps/${appId}`
            : `/api/assets/${this.assetId}/pins`;
        this._busy = true;
        this._error = '';
        try {
            const data = pin ? await api.put(path, pin) : await api.delete(path);
            this._apps = data?.apps || this._apps;
            this._editing = '';
        } catch (err) {
            this._error = err.message;
        } finally {
            this._busy = false;
        }
    }

    // The pins an app can take: the default, then each pinnable source's
    // latest and each of its versions. A choice is encoded as "source|version",
    // "source|latest" or "default".
    _choices() {
        const choices = [{ value: 'default', label: pinLabel(null) }];
        for (const g of versionGroups(this.versions || [])) {
            if (g.source === 'upload') continue;
            choices.push({
                value: `${g.source}|latest`,
                label: pinLabel({ source: g.source, version: null }),
            });
            for (const v of g.versions) {
                choices.push({
                    value: `${g.source}|${v.version}`,
                    label: pinLabel({ source: g.source, version: v.version }),
                });
            }
        }
        return choices;
    }

    _choiceOf(pin) {
        if (!pin) return 'default';
        return `${pin.source}|${pin.version ?? 'latest'}`;
    }

    _save(appId) {
        if (this._choice === 'default') return this.setPin(appId, null);
        const [source, version] = this._choice.split('|');
        return this.setPin(appId, {
            source,
            version: version === 'latest' ? null : Number(version),
        });
    }

    render() {
        if (this._error && this._apps.length === 0)
            return html`<p class="text-danger small mb-0">${this._error}</p>`;
        if (this._apps.length === 0) return html`<p class="text-muted small mb-0">No apps.</p>`;
        return html`
            <table class="table table-sm align-middle">
                <thead>
                    <tr><th>App</th><th>Pin</th><th>Serves</th><th></th></tr>
                </thead>
                <tbody>
                    ${this._apps.map(
                        (a) => html`
                        <tr>
                            <td>${a.name} <span class="muted">${a.slug}</span></td>
                            <td>
                                ${
                                    this._editing === a.app_id
                                        ? html`<select class="form-select form-select-sm" style="max-width: 220px;"
                                            @change="${(e) => (this._choice = e.target.value)}">
                                            ${this._choices().map((c) => html`<option value="${c.value}" ?selected="${this._choice === c.value}">${c.label}</option>`)}
                                        </select>`
                                        : pinLabel(a.pin)
                                }
                            </td>
                            <td>${a.serves ? `${a.serves.source} v${a.serves.version}` : html`<span class="muted">Nothing</span>`}</td>
                            <td class="actions">
                                ${
                                    this._editing === a.app_id
                                        ? html`
                                    <button class="btn btn-primary btn-sm" ?disabled="${this._busy}" @click="${() => this._save(a.app_id)}">Save</button>
                                    <button class="btn btn-outline-secondary btn-sm" ?disabled="${this._busy}" @click="${() => (this._editing = '')}">Cancel</button>
                                `
                                        : html`
                                    <button class="btn btn-outline-secondary btn-sm" ?disabled="${this._busy}"
                                        @click="${() => {
                                            this._editing = a.app_id;
                                            this._choice = this._choiceOf(a.pin);
                                        }}">Change</button>
                                    ${a.pin ? html`<button class="btn btn-outline-secondary btn-sm" ?disabled="${this._busy}" @click="${() => this.setPin(a.app_id, null)}">Reset</button>` : nothing}
                                `
                                }
                            </td>
                        </tr>
                    `,
                    )}
                </tbody>
            </table>
            ${this._error ? html`<p class="text-danger small mt-1 mb-0">${this._error}</p>` : nothing}
        `;
    }
}

customElements.define('app-pins-panel', AppPinsPanel);
